import { RefObject, useContext, useMemo, useState } from "react";
import { Button, Chip, Grid, SelectChangeEvent, Stack, Tooltip, Typography } from "@mui/material";
import { UseFieldArrayAppend } from "react-hook-form";
import {
  Competency_Map_Status,
  CompetencyMapQuery,
  useCompetencyMapHeaderQuery,
  useKnowledgeBlockHeaderQuery,
  useUpdateCompetencyMapStatusMutation,
} from "@generated/graphql";
import useSetTabTitle from "hooks/useSetTabTitle";
import useCreateTesting from "hooks/useCreateTesting";
import { CompanyContext } from "context/companyContext";
import ButtonWithPermissions, { generateUnavailableText } from "components/buttons/ButtonWithPermissions";
import { COMPETENCY_MAP_STATUS_TITLE, COMPETENCY_MAP_STATUSES } from "constants/global";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import DownloadCompetencyMapButton from "components/buttons/DownloadCompetencyMapButton";
import { parseApolloError } from "helpers/errorsParser";
import GenerateQuestionsForCMModal from "components/modals/GenerateQuestionsForCMModal";
import BackPageButton from "components/buttons/BackPageButton";
import { useSearchParams } from "react-router-dom";
import qs from "qs";
import ROUTES from "constants/routes";
import Select, { ISelectItem } from "components/inputs/Select";
import { CompetencyMapInputs, RefetchCompetencyMapGenQuestionsLog } from "./types";

interface IHeaderProps {
  competencyMap: CompetencyMapQuery["competencyMap"];
  disabled: boolean;
  append: UseFieldArrayAppend<CompetencyMapInputs, "knowledgeBlocks">;
  isCreate: boolean;
  isEditable: boolean;
  headerRef: RefObject<HTMLDivElement>;
  refetchCompetencyMapGenQuestionsLog: RefetchCompetencyMapGenQuestionsLog;
  handleArchiveMap: () => void;
}

const Header = ({
  competencyMap,
  disabled,
  append,
  isCreate,
  isEditable,
  headerRef,
  refetchCompetencyMapGenQuestionsLog,
  handleArchiveMap,
}: IHeaderProps) => {
  const [openGenerateQuestionsModal, setOpenGenerateQuestionsModal] = useState<boolean>(false);
  const { createTesting } = useCreateTesting({ competencyMap });
  const { addToast } = useContext(ToastContext);
  const { isCreateAvailable, currentCompany } = useContext(CompanyContext);
  const [searchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]);
  const { knowledgeBlockId, redirectCompetencyMapId } = params as {
    knowledgeBlockId?: string;
    redirectCompetencyMapId?: string;
  };

  const { data: knowledgeBlockHeaderData } = useKnowledgeBlockHeaderQuery({
    variables: { knowledgeBlockId: knowledgeBlockId! },
    skip: !knowledgeBlockId,
  });
  const knowledgeBlockHeader = knowledgeBlockHeaderData?.knowledgeBlock;

  const { data: parentCompetencyMapHeaderData } = useCompetencyMapHeaderQuery({
    variables: { competencyMapId: redirectCompetencyMapId! },
    skip: !redirectCompetencyMapId,
  });
  const parentCompetencyMapHeader = parentCompetencyMapHeaderData?.competencyMap;
  const createAssessmentUnavailableText = generateUnavailableText(
    isCreateAvailable,
    currentCompany,
    "создания тестирования"
  );
  const [updateMapStatus] = useUpdateCompetencyMapStatusMutation();

  const title = `Карта компетенций ${competencyMap?.title ? ` «${competencyMap.title}»` : ""}`;
  useSetTabTitle(title, competencyMap?.title || "Новая карта компетенций");

  const handleAddBlock = () => {
    append({ title: "", knowledgeBlockCompetences: undefined });
  };

  const handleChangeStatus = (event: SelectChangeEvent<ISelectItem>) => {
    if (!competencyMap?.id) return;

    const status = event.target.value as Competency_Map_Status;

    if (status === Competency_Map_Status.Archive) {
      handleArchiveMap();
    } else {
      updateMapStatus({
        variables: {
          input: {
            id: competencyMap.id,
            status: event.target.value as Competency_Map_Status,
          },
        },
      })
        .then(() => {
          addToast({ type: ToastTypeEnum.SUCCESS });
        })
        .catch((e) => parseApolloError(e, { addToast }));
    }
  };

  const getBackPageButtonProps = () => {
    if (knowledgeBlockId) {
      return {
        text: `Блок знаний «${knowledgeBlockHeader?.title}»`,
        link: `/${ROUTES.KNOWLEDGE_BLOCKS}/view/${knowledgeBlockId}`,
      };
    }

    if (redirectCompetencyMapId) {
      return {
        text: `Карта компетенций «${parentCompetencyMapHeader?.title}»`,
        link: `/${ROUTES.COMPETENCE_MAPS}/view/${redirectCompetencyMapId}`,
      };
    }

    return {
      text: "Список карт компетенций",
      link: `/${ROUTES.COMPETENCE_MAPS}`,
    };
  };

  const isExaminationCreationDisabled = competencyMap?.status !== Competency_Map_Status.Approved;

  return (
    <>
      <Stack spacing={1.5}>
        {competencyMap?.status === Competency_Map_Status.Archive && (
          <Stack
            spacing={1}
            alignItems='flex-start'
            sx={{
              width: "fit-content",
              padding: 1.5,
              backgroundColor: "white",
              borderRadius: 2,
              boxShadow: "0px 0px 7.3px 0px rgba(164, 184, 253, 0.24)",
            }}>
            <Chip
              label='Карта в архиве'
              sx={{ backgroundColor: "background.default", color: "base.500" }}
            />
            <Typography>Запуск тестирований и смена статуса недоступны</Typography>
          </Stack>
        )}
        <Stack
          ref={headerRef}
          direction='row'
          justifyContent='space-between'
          spacing={1}
          sx={{ pb: 1 }}>
          <Stack>
            <BackPageButton {...getBackPageButtonProps()} />
            <Typography
              variant='h20'
              sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
          </Stack>
          <Stack
            direction='row'
            spacing={1}
            useFlexGap
            alignItems='start'
            sx={{ maxWidth: 490, minWidth: { md: 490 } }}>
            <Grid
              container
              spacing={0.5}>
              <Grid
                item
                xs={12}
                md={6}>
                <ButtonWithPermissions
                  buttonText='Добавить компетенцию'
                  sx={{ width: "100%", backgroundColor: "white", color: "text.primary", "&:hover": { color: "white" } }}
                  onClick={handleAddBlock}
                  disabled={!isEditable}
                  size='small'
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}>
                {!isCreate && (
                  <Select
                    fullWidth
                    sx={{ width: "100%" }}
                    items={COMPETENCY_MAP_STATUSES.filter((status) => !status.hide)}
                    value={competencyMap?.status || undefined}
                    onChange={handleChangeStatus}
                    disabled={disabled || competencyMap?.status === Competency_Map_Status.Archive}
                    inputProps={{
                      sx: { paddingLeft: 4, py: 0.4 },
                    }}
                  />
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={6}>
                <Button
                  sx={{ width: "100%", whiteSpace: "nowrap" }}
                  variant='contained'
                  color='primary'
                  size='small'
                  onClick={() => setOpenGenerateQuestionsModal(true)}>
                  Сгенерировать вопросы
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}>
                <Tooltip
                  title={
                    isExaminationCreationDisabled
                      ? `Создание тестирования доступно только в\u00A0статусе «${COMPETENCY_MAP_STATUS_TITLE.APPROVED}»`
                      : undefined
                  }>
                  <span>
                    <ButtonWithPermissions
                      buttonText='Создать тестирование'
                      onClick={createTesting}
                      size='small'
                      tooltipText={createAssessmentUnavailableText}
                      disabled={isExaminationCreationDisabled}
                      sx={{ width: "100%" }}
                    />
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
            <DownloadCompetencyMapButton competencyMap={competencyMap} />
          </Stack>
        </Stack>
      </Stack>
      {openGenerateQuestionsModal && (
        <GenerateQuestionsForCMModal
          competencyMap={competencyMap}
          open={openGenerateQuestionsModal}
          onClose={() => setOpenGenerateQuestionsModal(false)}
          refetchCompetencyMapGenQuestionsLog={refetchCompetencyMapGenQuestionsLog}
        />
      )}
    </>
  );
};

export default Header;
